import type { PaymentGateway } from '../domain/PaymentsAccount'
import type { PaymentIntent } from '../domain/PaymentIntent'

export type PaymentUsage = 'setup-card' | 'payment'

export class PaymentSessionValidationError extends Error {

    constructor(message: string) {
        super(message)
        this.name = 'PaymentSessionValidationError'
    }
}

/**
 * Gateway-agnostic abstraction for a mounted payment UI (Stripe Elements, Ryft, etc.).
 *
 * A `PaymentSession` is created for a specific intent (setup or payment) and is
 * responsible for mounting the payment UI, surfacing validation/error state, and
 * confirming the payment when the user presses the "Pay" button.
 *
 * Wallet payments (e.g. Google Pay) can complete automatically without the user
 * pressing the "Pay" button. When that happens, the session fires `onWalletConfirmed`
 * with the resulting intent / session id. Gateways that don't support wallet
 * auto-confirmation will simply never fire this callback.
 */
export interface PaymentSession {

    /** The underlying payment gateway. */
    readonly gateway: PaymentGateway

    /**
     * Whether this session may auto-confirm via a wallet button.
     * Callers can use this to decide whether to render a wallet container.
     */
    readonly walletPaymentAvailable: boolean

    /** Whether confirmation leaves this page for a provider-hosted payment form. */
    readonly usesHostedCheckout?: boolean

    readonly requiresFutureChargeConsent?: boolean

    /** Set the callback fired when the card form validity changes. */
    setOnValidityChange(handler: (isValid: boolean) => void): void

    /**
     * Set the callback fired when a wallet payment (e.g. Google Pay) completes
     * automatically. The handler receives the resulting intent / session id.
     */
    setOnWalletConfirmed(handler: (intentId: string) => void): void

    /** Set the callback fired when a recoverable error occurs in the payment UI. */
    setOnError(handler: (message: string) => void): void

    /**
     * Confirm the payment / setup using the manually-entered card details.
     * Returns the resulting intent / session id, or throws on failure.
     */
    confirm(billingEmailAddress?: string): Promise<string>

    /** Tear down any mounted UI and listeners. */
    destroy(): void
}

/** Options describing where to mount the payment UI and which intent it serves. */
export interface PaymentSessionOptions {
    /** Provider-neutral intent returned by the corresponding backend endpoint. */
    intent: PaymentIntent

    /** Whether this session is for saving card details or taking a payment. */
    usage: PaymentUsage

    /** Save a cancellation card while authorizing an upfront order payment. */
    saveCardWithPayment?: boolean

    /** Display name shown by wallet checkout, defaulting to the venue name. */
    merchantName?: string

    hasFutureChargeConsent?: () => boolean

    requiresBillingEmail?: boolean

    /** CSS selector for the container that should host the card-entry form. */
    cardFormSelector: string

    /**
     * Supplies the current billing email when a wallet initiates payment without
     * going through `confirm()`.
     */
    getBillingEmailAddress?: () => string | undefined

    /**
     * CSS selector for the container that should host an Apple Pay wallet button.
        * The payment session decides whether Apple Pay should actually be mounted.
     */
    applePayWalletButtonSelector?: string

    /**
     * CSS selector for the container that should host a Google Pay wallet button.
     * Ignored when the active gateway does not support wallet payments.
     */
    googlePayWalletButtonSelector?: string
}
